import React from 'react';
import PhoneScannerScreen from '../PhoneScannerScreen';
import { stripSerial } from '../../utils/scan';
import WorkbenchLayout from './WorkbenchLayout';
import RosterRail from './RosterRail';
import CheckRow from './CheckRow';
import KeyHintBar from './KeyHintBar';
import CHECK_OPTIONS from './checkOptions';
import QcStatTiles from './QcStatTiles';

// Goods-inwards QC import. Three steps: setup -> checking -> summary.
//
// Why this is a component rather than inline in App.js like most of the app: every setSt PUTs
// the entire state blob to Azure, so per-check results are kept in LOCAL state here and only
// written to st twice — on the (slow) draft autosave and at Finish. Keeping ~15 pieces of
// session state out of the App component also stops the whole app re-rendering on every tap.
export default function QCImportPanel({
  st, setSt, audit, account, userKey,
  getQCDefs, getQCPF,
  lbBySerial, normSerial, buildLbIndex, lbApiUrl,
  finishQCImport, printQCSessionReport,
  isReadOnly, isTablet, darkMode, OW, DM, ss,
  FilterSelect, DatePicker, IC,
  buzzSuccess, buzzError,
  onFinished, onViewRma, onCancel, dirtyRef,
  QC_DRAFT_AUTOSAVE_MS, BATCH_AUTO_ADVANCE_MS,
}) {
  const [qcView, setQcView] = React.useState('setup');
  // setup
  const [qcBatchNo, setQcBatchNo] = React.useState('');
  const [qcType, setQcType] = React.useState('');
  const [qcSimType, setQcSimType] = React.useState('');
  const [qcSimType2, setQcSimType2] = React.useState('');
  const [qcPurchaseDate, setQcPurchaseDate] = React.useState('');
  const [qcAllowMixedType, setQcAllowMixedType] = React.useState(false);

  // ── Manifest ────────────────────────────────────────────────────────────
  // The supplier's manifest becomes the QC roster, so a short or wrong
  // delivery is caught before the invoice is paid. The file supplies serials
  // only — everything else about a unit comes from Bench or from the check
  // itself, which is why the roster column reads "Matched in Bench".
  const [qcManifest, setQcManifest] = React.useState(null);   // {name,rows,invalid,dupes}
  const [qcOrdered, setQcOrdered] = React.useState('');       // units ordered on the PO
  const [qcManifestErr, setQcManifestErr] = React.useState('');
  const [qcPasteOpen, setQcPasteOpen] = React.useState(false);
  const [qcPasteText, setQcPasteText] = React.useState('');
  // checking — qcIdx -1 means "awaiting a scan", >=0 means "checking qcDevs[qcIdx]"
  const [qcDevs, setQcDevs] = React.useState([]);
  const [qcIdx, setQcIdx] = React.useState(-1);
  const [qcRes, setQcRes] = React.useState({});
  const [qcNotes, setQcNotes] = React.useState({});
  const [qcScanBuf, setQcScanBuf] = React.useState('');
  const [qcScanErr, setQcScanErr] = React.useState('');
  const [qcCamOpen, setQcCamOpen] = React.useState(false);
  // Roster rail filter and the keyboard cursor within the open unit's checklist.
  const [rosterFilter, setRosterFilter] = React.useState('all');
  const [focusedCheck, setFocusedCheck] = React.useState(0);
  const [qcDraftSavedAt, setQcDraftSavedAt] = React.useState(null);
  // finish
  const [qcSaving, setQcSaving] = React.useState(false);
  const [qcSessionId, setQcSessionId] = React.useState(null);
  const [qcStartedAt, setQcStartedAt] = React.useState(null);
  const [qcDone, setQcDone] = React.useState(null);
  const [qcSyncing, setQcSyncing] = React.useState(false);
  const [qcSyncMsg, setQcSyncMsg] = React.useState('');
  const [draftOffer, setDraftOffer] = React.useState(null);

  const scanRef = React.useRef(null);
  const noteRef = React.useRef(null);
  const qcDevsRef = React.useRef(qcDevs);
  React.useEffect(() => { qcDevsRef.current = qcDevs; }, [qcDevs]);

  // Purchase date is required to start a QC session. Warranty on a goods-inwards unit is
  // calculated from it, and a batch imported without one leaves every device permanently
  // "no purchase date recorded" on the RMA screen — unrecoverable once the delivery
  // paperwork is filed. A future date is rejected as a typo.
  const qcPurchaseDateErr = (() => {
    if (!qcPurchaseDate) return '';
    const d = new Date(qcPurchaseDate + 'T00:00:00');
    if (isNaN(d.getTime())) return 'Enter a valid date.';
    const today = new Date(); today.setHours(23, 59, 59, 999);
    if (d > today) return "Purchase date can't be in the future.";
    return '';
  })();

  // SIM Type 2 only exists on RH2 — buildImportedDevice blanks it for every other type, so
  // requiring it globally would make the form unfillable. It becomes required as soon as an
  // RH2 is in the batch, which with mixed types on can happen mid-scan via changeDevType,
  // hence checking the scanned devices and not just the batch type picked at setup.
  const qcNeedsSim2 = qcType === 'RH2' || qcDevs.some(d => d.type === 'RH2');
  const qcSimMissing = !qcSimType.trim() || (qcNeedsSim2 && !qcSimType2.trim());

  const curDev = qcIdx >= 0 ? qcDevs[qcIdx] : null;
  const curDefs = curDev ? getQCDefs(curDev.type) : [];
  const curPF = curDev ? getQCPF(curDev.type) : [];

  // ── Draft resume ──────────────────────────────────────────────────────────
  React.useEffect(() => {
    const d = (st.qcDrafts || {})[userKey];
    if (d && d.qcDevs && d.qcDevs.length && qcView === 'setup' && !qcDevs.length) setDraftOffer(d);
  }, []); // eslint-disable-line

  const resumeDraft = () => {
    const d = draftOffer; if (!d) return;
    setQcBatchNo(d.qcBatchNo || ''); setQcType(d.qcType || '');
    setQcSimType(d.qcSimType || ''); setQcSimType2(d.qcSimType2 || '');
    setQcPurchaseDate(d.qcPurchaseDate || ''); setQcAllowMixedType(!!d.qcAllowMixedType);
    setQcDevs(d.qcDevs || []); setQcRes(d.qcRes || {}); setQcNotes(d.qcNotes || {});
    setQcSessionId(d.qcSessionId || 'qc-' + Date.now());
    setQcStartedAt(d.qcStartedAt || new Date().toISOString());
    setQcDraftSavedAt(d.savedAt || null);
    setQcIdx(-1); setQcView('checking'); setDraftOffer(null);
  };
  const discardDraft = () => {
    setSt(s => ({ ...s, qcDrafts: { ...(s.qcDrafts || {}), [userKey]: null } }));
    setDraftOffer(null);
  };

  // Draft autosave. Deliberately slower than the test flow's 500ms — see QC_DRAFT_AUTOSAVE_MS.
  React.useEffect(() => {
    if (qcView !== 'checking' || !qcDevs.length) return;
    const tid = setTimeout(() => {
      const savedAt = new Date().toISOString();
      setSt(s => ({ ...s, qcDrafts: { ...(s.qcDrafts || {}), [userKey]: {
        qcBatchNo, qcType, qcSimType, qcSimType2, qcPurchaseDate, qcAllowMixedType,
        qcDevs, qcRes, qcNotes, qcSessionId, qcStartedAt, savedAt,
      } } }));
      // Mirrored into local state purely so the topbar can show "Draft saved
      // HH:MM". The persisted draft is unchanged — this is the same timestamp.
      setQcDraftSavedAt(savedAt);
    }, QC_DRAFT_AUTOSAVE_MS);
    return () => clearTimeout(tid);
  }, [qcRes, qcNotes, qcDevs, qcIdx, qcView]); // eslint-disable-line

  React.useEffect(() => {
    if (qcView !== 'checking' || !qcDevs.length) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = 'A QC session is in progress — your progress is saved as a draft.'; return e.returnValue; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [qcView, qcDevs.length]);

  React.useEffect(() => { if (dirtyRef) dirtyRef.current = qcView === 'checking' && qcDevs.length > 0; }, [qcView, qcDevs.length, dirtyRef]);

  React.useEffect(() => { if (qcView === 'checking' && qcIdx === -1 && scanRef.current) scanRef.current.focus(); }, [qcIdx, qcView]);

  // Opening a different unit restarts the keyboard cursor at its first check.
  React.useEffect(() => { setFocusedCheck(0); }, [qcIdx]);

  // ── Keyboard shortcuts ────────────────────────────────────────────────────
  // P / F / N answer the check under the cursor, arrows move it, Enter closes
  // the unit, Escape returns to the roster.
  //
  // Never fires while a text field has focus: the scan input takes bare serials
  // (which contain no letters, but a typed note certainly does) and the fault
  // box is where the operator types words like "no power" — an F there must be
  // an F, not a failed check. Modifier combinations are left to the browser.
  React.useEffect(() => {
    if (qcView !== 'checking' || qcIdx < 0) return;
    const onKey = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const el = e.target;
      const tag = el && el.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (el && el.isContentEditable)) {
        // Escape still works from the fault box, as a way back out of it.
        if (e.key === 'Escape' && el === noteRef.current) { el.blur(); e.preventDefault(); }
        return;
      }

      const dev = qcDevsRef.current[qcIdx];
      if (!dev) return;
      const defs = getQCDefs(dev.type);
      const pfIdx = [];
      defs.forEach((d, i) => { if (d.type === 'passfail') pfIdx.push(i); });

      if (e.key === 'Escape') { e.preventDefault(); setQcIdx(-1); setQcScanBuf(''); return; }
      if (e.key === 'Enter') { e.preventDefault(); setQcIdx(-1); setQcScanBuf(''); return; }

      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        if (!defs.length) return;
        e.preventDefault();
        setFocusedCheck(c => {
          const next = e.key === 'ArrowDown' ? c + 1 : c - 1;
          return Math.max(0, Math.min(defs.length - 1, next));
        });
        return;
      }

      const opt = CHECK_OPTIONS.find(o => o.key.toLowerCase() === e.key.toLowerCase());
      if (!opt) return;
      const def = defs[focusedCheck];
      // Only pass/fail checks are keyboard-answerable; a number check needs a value.
      if (!def || def.type !== 'passfail') return;
      e.preventDefault();
      setQcChk(dev.tmpId, def.id, opt.value);
      // Advance to the next unanswered pass/fail check, if there is one. This
      // deliberately does NOT skip or auto-answer anything: a fail still leaves
      // every remaining check to be answered, because the RMA needs the full list.
      const after = pfIdx.filter(i => i > focusedCheck);
      if (after.length) setFocusedCheck(after[0]);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [qcView, qcIdx, focusedCheck, getQCDefs]); // eslint-disable-line

  // ── Derived ───────────────────────────────────────────────────────────────
  const devResult = React.useCallback((d) => {
    const defs = getQCPF(d.type);
    const r = qcRes[d.tmpId] || {};
    const failedChecks = defs.filter(c => r[c.id] === 'fail').map(c => c.label);
    const answered = defs.filter(c => ['pass', 'fail', 'na'].includes(r[c.id])).length;
    const result = (defs.length ? failedChecks.length > 0 : !!d.manualFail) ? 'fail' : 'pass';
    return { defs, failedChecks, answered, unanswered: defs.length - answered, result, complete: defs.length ? answered === defs.length : true };
  }, [qcRes, getQCPF]);

  const qcStats = React.useMemo(() => {
    const rows = qcDevs.map(d => ({ d, ...devResult(d) }));
    const pass = rows.filter(r => r.result === 'pass').length;
    const byCheck = {};
    rows.forEach(r => r.failedChecks.forEach(l => { byCheck[l] = (byCheck[l] || 0) + 1; }));
    return {
      rows, total: rows.length, pass, fail: rows.length - pass,
      passRate: rows.length ? Math.round((pass / rows.length) * 100) : 0,
      unmatched: qcDevs.filter(d => !d.lbDeviceId).length,
      incomplete: rows.filter(r => r.unanswered > 0).length,
      topFails: Object.entries(byCheck).sort((a, b) => b[1] - a[1]),
    };
  }, [qcDevs, devResult]);

  // ── Scanning ──────────────────────────────────────────────────────────────
  // ── Manifest parsing ────────────────────────────────────────────────────
  // Deliberately forgiving: suppliers send a bare serial list as often as a
  // real CSV. Any column whose header looks like a serial is used; failing
  // that, the first column is. Serials run through stripSerial, the same
  // parser the scanner uses, so a manifest of scanned URLs works too.
  const parseManifest = (text, fileName) => {
    // Real packing slips are not CSVs. A Lightbug slip is a page of prose
    // ("Packing Slip - 21/10/2025", "Invoice 2751", "100 Units Shipped ...")
    // followed by one long comma-separated run of serials that PDF extraction
    // wraps mid-number: "...,43000\n126,..." is a single serial 43000126.
    // So: rejoin wrapped lines, tokenise on every delimiter, and keep only
    // tokens shaped like a serial. Anything else in the file is ignored.
    const raw = String(text || '').split(/\r?\n/);
    if (!raw.length) { setQcManifestErr('That file is empty.'); return; }

    // Rejoin a line to the previous one only when both sides look like part of
    // a serial run - previous ends in a digit or delimiter, this starts with a
    // digit. Prose lines are left intact so they tokenise away harmlessly.
    const blocks = [];
    for (const r of raw) {
      const line = r.trim();
      if (!line) { blocks.push(''); continue; }
      const prev = blocks.length ? blocks[blocks.length - 1] : '';
      // Join only when the wrap actually split something: the previous line
      // ended mid-token (its last token is too short to be a serial), or it
      // ended on a delimiter. A line of complete serials must NOT absorb the
      // next line, or a bare one-per-line list glues into a single number.
      const lastTok = prev.split(/[,;\t ]+/).pop() || '';
      const prevTruncated = /^[0-9]+$/.test(lastTok) && lastTok.length < 8;
      const prevOpen = /[,;\t]$/.test(prev);
      if (prev && (prevOpen || prevTruncated) && /^[0-9]/.test(line)) {
        blocks[blocks.length - 1] = prev + line;
      } else {
        blocks.push(line);
      }
    }

    const tokens = blocks.join('\n').split(/[,;\t\n\r ]+/).map(t => t.trim()).filter(Boolean);

    const seen = new Set();
    const rows = [], dupes = [], ignored = [];
    for (const t of tokens) {
      // A date like 21/10/2025 must not become a serial, but a scanned URL must.
      const looksUrl = /^https?:/i.test(t) || /\/sn\//i.test(t);
      if (!looksUrl && /[/\\]/.test(t)) { ignored.push(t); continue; }
      const serial = stripSerial(t).trim().replace(/[^A-Za-z0-9-]/g, '');
      // Serials here are 8 digits; allow a short alpha prefix and 6+ digits so
      // other suppliers' formats still parse. Everything else is prose.
      if (!/^[A-Za-z]{0,4}[0-9]{6,}$/.test(serial)) { ignored.push(t); continue; }
      const key = serial.toLowerCase();
      if (seen.has(key)) { dupes.push(serial); continue; }
      seen.add(key);
      rows.push({ serial });
    }

    if (!rows.length) {
      setQcManifestErr('No serials found in that file. It can be a packing slip, a CSV, or one serial per line.');
      return;
    }
    setQcManifestErr('');
    setQcManifest({ name: fileName || 'manifest', rows, invalid: [], dupes, ignored });
  };

  const onManifestFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = e => parseManifest(e.target.result, file.name);
    reader.onerror = () => setQcManifestErr('That file could not be read.');
    reader.readAsText(file);
  };

  // Roster preview: each manifest serial resolved against what Bench already
  // knows. Nothing is written here — this is a preview of what QC will walk.
  const qcRosterPreview = React.useMemo(() => {
    if (!qcManifest) return [];
    return qcManifest.rows.map(r => {
      const known = (st.devices || []).find(d => (d.serial || '').toLowerCase() === r.serial.toLowerCase());
      const lb = lbBySerial.current[normSerial(r.serial)] || {};
      const note = [lb.firmware ? 'FW ' + lb.firmware : null, qcSimType.trim() ? 'SIM ' + qcSimType.trim() : null]
        .filter(Boolean).join(' \u00b7 ');
      return {
        serial: r.serial,
        type: qcType || (known && known.type) || '\u2014',
        matched: known ? (known.location || 'In Bench') : lb.deviceId ? 'Lightbug only' : 'New to Bench',
        matchedTone: known ? 'warning' : 'neutral',
        note: note || 'No firmware in the file',
        muted: !note,
        state: known ? 'Already in Bench' : 'To check',
        stateTone: known ? 'warning' : 'neutral',
      };
    });
  }, [qcManifest, st.devices, qcType, qcSimType, lbBySerial, normSerial]);

  // Reconciliation against the purchase order.
  const qcReconcile = React.useMemo(() => {
    if (!qcManifest) return null;
    const onManifest = qcManifest.rows.length;
    const ordered = parseInt(qcOrdered, 10);
    const diff = Number.isFinite(ordered) ? onManifest - ordered : null;
    return { onManifest, ordered: Number.isFinite(ordered) ? ordered : null, diff };
  }, [qcManifest, qcOrdered]);

  const qcCommitSerial = (raw) => {
    const serial = stripSerial(raw).trim();
    if (!serial) return;
    const list = qcDevsRef.current;
    const dupIdx = list.findIndex(d => d.serial.toLowerCase() === serial.toLowerCase());
    if (dupIdx >= 0) {
      buzzError && buzzError();
      setQcScanErr(`${serial} has already been checked in this batch — jumped to it.`);
      setQcIdx(dupIdx); setQcScanBuf('');
      return;
    }
    const exists = (st.devices || []).find(d => d.serial.toLowerCase() === serial.toLowerCase());
    if (exists && !window.confirm(`Serial ${serial} already exists in OWL Bench (${exists.location || '—'}). Add anyway?`)) {
      setQcScanBuf(''); return;
    }
    const lb = lbBySerial.current[normSerial(serial)] || {};
    const tmpId = 'tmp-' + Date.now() + Math.random();
    const dev = { tmpId, serial, type: qcType, lbDeviceId: lb.deviceId || '', firmware: lb.firmware || '', scannedAt: new Date().toISOString(), alreadyExists: !!exists, manualFail: false };
    const seed = {};
    getQCDefs(dev.type).forEach(c => { seed[c.id] = c.type === 'number' ? 0 : ''; });
    setQcDevs(p => [...p, dev]);
    setQcRes(r => ({ ...r, [tmpId]: seed }));
    setQcNotes(n => ({ ...n, [tmpId]: '' }));
    setQcIdx(list.length);
    setQcScanBuf(''); setQcScanErr('');
    buzzSuccess && buzzSuccess();
  };

  const qcOnScanInput = (val) => {
    const parts = val.split(/[\n,]+/);
    if (parts.length > 1) { parts.slice(0, -1).forEach(qcCommitSerial); setQcScanBuf(parts[parts.length - 1]); }
    else setQcScanBuf(val);
  };

  const setQcChk = (tmpId, checkId, v) => setQcRes(r => ({ ...r, [tmpId]: { ...r[tmpId], [checkId]: v } }));

  // Auto-advance only when a device passes cleanly. A failure holds the screen so the operator
  // can type the note that becomes the RMA fault description.
  React.useEffect(() => {
    if (qcView !== 'checking' || qcIdx < 0) return;
    const d = qcDevs[qcIdx]; if (!d) return;
    const defs = getQCPF(d.type);
    if (!defs.length) return;
    const r = qcRes[d.tmpId] || {};
    if (!defs.every(c => ['pass', 'fail', 'na'].includes(r[c.id]))) return;
    if (defs.some(c => r[c.id] === 'fail')) return;
    const tid = setTimeout(() => { setQcIdx(-1); setQcScanBuf(''); }, BATCH_AUTO_ADVANCE_MS);
    return () => clearTimeout(tid);
  }, [qcRes, qcIdx, qcView]); // eslint-disable-line

  const removeDev = (tmpId) => {
    const d = qcDevs.find(x => x.tmpId === tmpId);
    if (!d || !window.confirm(`Remove ${d.serial} from this QC batch?`)) return;
    setQcDevs(p => p.filter(x => x.tmpId !== tmpId));
    setQcRes(r => { const n = { ...r }; delete n[tmpId]; return n; });
    setQcNotes(n => { const c = { ...n }; delete c[tmpId]; return c; });
    setQcIdx(-1);
  };

  const changeDevType = (tmpId, newType) => {
    setQcDevs(p => p.map(d => d.tmpId === tmpId ? { ...d, type: newType } : d));
    // Re-seed for the new type, keeping any answers whose check id exists in both lists.
    setQcRes(r => {
      const prev = r[tmpId] || {}; const seed = {};
      getQCDefs(newType).forEach(c => { seed[c.id] = c.id in prev ? prev[c.id] : (c.type === 'number' ? 0 : ''); });
      return { ...r, [tmpId]: seed };
    });
  };

  const qcSyncLightbug = async () => {
    setQcSyncing(true); setQcSyncMsg('');
    try {
      const r = await fetch(lbApiUrl('?action=summary'));
      const data = r.ok ? await r.json() : null;
      if (!Array.isArray(data)) throw new Error('bad response');
      lbBySerial.current = buildLbIndex(data);
      let matched = 0;
      setQcDevs(p => p.map(d => {
        const lb = lbBySerial.current[normSerial(d.serial)];
        if (lb) matched++;
        return lb ? { ...d, lbDeviceId: lb.deviceId, firmware: lb.firmware } : d;
      }));
      setQcSyncMsg(`Matched ${matched} of ${qcDevsRef.current.length} scanned serials`);
    } catch (e) {
      setQcSyncMsg('Sync failed — check your connection and try again.');
    } finally { setQcSyncing(false); }
  };

  // ── Finish ────────────────────────────────────────────────────────────────
  const goToSummary = () => {
    if (!qcDevs.length) return;
    if (qcStats.incomplete > 0 && !window.confirm(`${qcStats.incomplete} device${qcStats.incomplete === 1 ? ' has' : 's have'} unanswered checks. They'll be recorded as N/A. Continue?`)) return;
    const noNote = qcStats.rows.filter(r => r.result === 'fail' && !(qcNotes[r.d.tmpId] || '').trim()).length;
    if (noNote > 0 && !window.confirm(`${noNote} failed device${noNote === 1 ? ' has' : 's have'} no note. The RMA fault description will list only the failed check names. Continue?`)) return;
    setQcView('summary');
  };

  const doImport = async () => {
    if (qcSaving || !qcDevs.length) return;
    if (!qcPurchaseDate || qcPurchaseDateErr || qcSimMissing) return;
    setQcSaving(true);
    // One last Lightbug sync — a unit unmatched when scanned is often provisioned by now.
    try { await qcSyncLightbug(); } catch (e) { /* non-fatal: import proceeds with blank IDs */ }
    const res = finishQCImport({
      batchNo: qcBatchNo.trim(), type: qcType, simType: qcSimType, simType2: qcSimType2,
      purchaseDate: qcPurchaseDate, sessionId: qcSessionId, startedAt: qcStartedAt,
      qcDevs: qcDevsRef.current, qcRes, qcNotes,
    });
    setQcSaving(false);
    if (res) setQcDone(res);
  };

  const resetAll = () => {
    setQcView('setup'); setQcDevs([]); setQcRes({}); setQcNotes({}); setQcIdx(-1);
    setQcBatchNo(''); setQcType(''); setQcSimType(''); setQcSimType2(''); setQcPurchaseDate('');
    setQcSessionId(null); setQcStartedAt(null); setQcDone(null); setQcScanErr(''); setQcSyncMsg('');
    setQcDraftSavedAt(null); setRosterFilter('all'); setFocusedCheck(0);
  };

  // ── Shared bits ───────────────────────────────────────────────────────────
  const pill = (text, kind) => (
    <span style={{ display: 'inline-block', padding: '1px 7px', borderRadius: 8, fontSize: 10, fontWeight: 600,
      background: `var(--${kind}-fill)`, color: `var(--${kind}-text)`, border: `1px solid var(--${kind}-border)` }}>{text}</span>
  );

  const statsBlock = (stats) => (
    <>
      <div style={{ marginBottom: 14 }}>
        <QcStatTiles tiles={[
          { label: "Devices QC'd", value: stats.total },
          { label: 'Passed', value: stats.pass, color: 'var(--success-text)' },
          { label: 'Failed', value: stats.fail, color: 'var(--danger-text)' },
          { label: 'Pass rate', value: stats.passRate + '%' },
          stats.unmatched > 0 && { label: 'No Lightbug ID', value: stats.unmatched, color: 'var(--warning-text)' },
        ]}/>
      </div>
      {stats.topFails.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-default)', marginBottom: 6 }}>Failure breakdown</div>
          {stats.topFails.map(([label, n]) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <span style={{ fontSize: 11, color: 'var(--text-muted)', minWidth: 150 }}>{label}</span>
              <div style={{ flex: 1, height: 8, borderRadius: 4, background: 'var(--surface-inset)', overflow: 'hidden' }}>
                <div style={{ width: (stats.fail ? Math.round((n / stats.fail) * 100) : 0) + '%', height: '100%', background: 'var(--danger-text)' }} />
              </div>
              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-default)', minWidth: 64, textAlign: 'right' }}>
                {n} ({stats.fail ? Math.round((n / stats.fail) * 100) : 0}%)
              </span>
            </div>
          ))}
        </div>
      )}
    </>
  );

  // ── Render: draft offer ───────────────────────────────────────────────────
  if (draftOffer) {
    return (
      <div style={{ padding: 14, borderRadius: 'var(--radius-xl)', background: 'var(--warning-fill)', border: '1px solid var(--warning-border)' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--warning-text)', marginBottom: 4 }}>Unfinished QC session</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>
          Batch <strong>{draftOffer.qcBatchNo || '—'}</strong> · {(draftOffer.qcDevs || []).length} device{(draftOffer.qcDevs || []).length === 1 ? '' : 's'} checked
          {draftOffer.savedAt ? ` · saved ${new Date(draftOffer.savedAt).toLocaleString('en-GB')}` : ''}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={resumeDraft} className="btn btn-primary btn-sm">Resume</button>
          <button onClick={discardDraft} className="btn btn-ghost btn-sm">Discard</button>
        </div>
      </div>
    );
  }

  // ── Render: setup ─────────────────────────────────────────────────────────
  if (qcView === 'setup') {
    const defs = qcType ? getQCDefs(qcType) : [];
    // Card + field helpers, matching the redesign's import screen: 44px card
    // header with a 16px/600 title, 12px field labels, 36px controls.
    const card = (title, body, sub) => (
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ minHeight: 44, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '10px 16px', borderBottom: '1px solid var(--border-default)' }}>
          <span style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>{title}</span>
          {sub && <span style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>{sub}</span>}
        </div>
        <div style={{ padding: 16 }}>{body}</div>
      </div>
    );
    const field = (label, control, help, helpTone) => (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-muted)' }}>{label}</span>
        {control}
        {help && <span style={{ fontSize: 12, lineHeight: '16px', color: helpTone || 'var(--text-muted)' }}>{help}</span>}
      </div>
    );

    return (
      <>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
          {onCancel && (
            <button
              type="button"
              aria-label="Back to Goods-in QC"
              title="Back to Goods-in QC"
              onClick={onCancel}
              style={{ width: 32, height: 32, flexShrink: 0, marginTop: 2, border: '1px solid var(--border-default)',
                borderRadius: 6, background: 'var(--surface-default)', color: 'var(--text-muted)', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface-raised)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'var(--surface-default)'; }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="19" y1="12" x2="5" y2="12" />
                <polyline points="12 19 5 12 12 5" />
              </svg>
            </button>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.3px', color: 'var(--text-primary)' }}>Import a delivery</div>
            <div style={{ fontSize: 14, lineHeight: '18px', color: 'var(--text-muted)', marginTop: 4, maxWidth: 700 }}>
              Every unit is walked through its QC checklist as it is booked in, so a short or faulty
              delivery is caught before the invoice is paid.
            </div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: isTablet ? '1fr' : '400px 1fr', gap: 16, alignItems: 'start' }}>
          {/* Left column: the delivery itself */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {card('Delivery', (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {field('Purchase order', (
                  <input value={qcBatchNo} onChange={e => setQcBatchNo(e.target.value)}
                    placeholder="e.g. PO-12345 / supplier ref" className="input" />
                ))}
                {field('Device type', (
                  <FilterSelect value={qcType} onChange={setQcType} darkMode={darkMode} OW={OW} DM={DM}
                    options={[{ value: '', label: 'Select...' }, ...(st.deviceTypes || []).map(t => ({ value: t, label: t }))]} />
                ))}
                {field('SIM 1', (
                  <input value={qcSimType} onChange={e => setQcSimType(e.target.value)}
                    placeholder="e.g. Webbing" className="input" />
                ))}
                {qcNeedsSim2 && field('SIM 2', (
                  <input value={qcSimType2} onChange={e => setQcSimType2(e.target.value)}
                    placeholder="e.g. Webbing" className="input"
                    style={!qcSimType2.trim() ? { borderColor: 'var(--danger-border)' } : undefined} />
                ), !qcSimType2.trim() ? 'Required. Both SIM types are recorded against every unit in the batch.' : null,
                   !qcSimType2.trim() ? 'var(--danger-text)' : null)}
                {field('Purchase date', (
                  <DatePicker value={qcPurchaseDate} onChange={e => setQcPurchaseDate(e.target.value)} darkMode={darkMode} OW={OW} DM={DM} />
                ), qcPurchaseDateErr || 'Warranty on every unit in the batch is calculated from this.',
                   qcPurchaseDateErr ? 'var(--danger-text)' : null)}
                {field('Units ordered', (
                  <input value={qcOrdered} onChange={e => setQcOrdered(e.target.value.replace(/[^0-9]/g, ''))}
                    inputMode="numeric" placeholder="e.g. 220" className="input" />
                ), 'From the purchase order. Used to reconcile the manifest.')}

                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 2 }}>
                  <button onClick={() => setQcAllowMixedType(v => !v)} role="switch" aria-checked={qcAllowMixedType}
                    style={{ width: 36, height: 20, borderRadius: 10, border: '1px solid var(--border-default)', cursor: 'pointer', padding: 2,
                      background: qcAllowMixedType ? 'var(--success-text)' : 'var(--surface-inset)', display: 'flex',
                      justifyContent: qcAllowMixedType ? 'flex-end' : 'flex-start', alignItems: 'center', transition: 'background 150ms' }}>
                    <span style={{ width: 14, height: 14, borderRadius: '50%', background: '#fff', display: 'block' }} />
                  </button>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Allow mixed device types in this batch</span>
                </div>
              </div>
            ))}

            {card('Checks per unit', (
              defs.length > 0 ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {defs.map(c => (
                    <span key={c.id} style={{ display: 'inline-flex', fontSize: 12, color: 'var(--text-primary)',
                      background: 'var(--surface-inset)', border: '1px solid var(--border-default)', borderRadius: 6, padding: '3px 8px' }}>{c.label}</span>
                  ))}
                </div>
              ) : qcType ? (
                <div style={{ fontSize: 12, color: 'var(--warning-text)', lineHeight: '16px' }}>
                  No QC checks are defined for {qcType}. Add them in <strong>Settings &rarr; Device Types &rarr; Edit QC checks</strong>,
                  or continue and record a pass/fail per unit.
                </div>
              ) : (
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Choose a device type to see the checks that will run.</div>
              )
            ), qcType && defs.length > 0 ? defs.length + ' check' + (defs.length === 1 ? '' : 's') + ' on each ' + qcType : null)}
          </div>

          {/* Right column: the manifest, then what happens next */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Manifest — the supplier's file becomes the QC roster, so a short
                delivery is caught before the invoice is paid. */}
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ height: 44, display: 'flex', alignItems: 'center', gap: 10, padding: '0 16px', borderBottom: '1px solid var(--border-default)' }}>
                <span style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>Manifest</span>
                <div style={{ flex: 1 }} />
                {qcManifest && (
                  <button className="btn btn-ghost btn-sm"
                    onClick={() => { setQcManifest(null); setQcManifestErr(''); }}>Remove</button>
                )}
                <button className="btn btn-ghost btn-sm"
                  onClick={() => { setQcPasteOpen(o => !o); setQcManifestErr(''); }}>
                  {qcPasteOpen ? 'Close' : 'Paste text'}
                </button>
                <label className="btn btn-ghost btn-sm" style={{ cursor: 'pointer', marginBottom: 0 }}>
                  {qcManifest ? 'Replace file' : 'Upload a file'}
                  <input type="file" accept=".csv,.txt,text/csv,text/plain" style={{ display: 'none' }}
                    onChange={e => { onManifestFile(e.target.files && e.target.files[0]); e.target.value = ''; }} />
                </label>
              </div>

              {qcManifestErr && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px',
                  background: 'var(--danger-fill)', borderBottom: '1px solid var(--danger-border)' }}>
                  <span style={{ fontSize: 14, color: 'var(--danger-text)' }}>{qcManifestErr}</span>
                </div>
              )}

              {qcPasteOpen && (
                <div style={{ padding: 16, borderBottom: '1px solid var(--border-subtle)' }}>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8, lineHeight: '16px' }}>
                    Open the packing slip, select all (Ctrl+A) and copy (Ctrl+C), then paste below.
                    Serials split across lines are rejoined, and everything that is not a serial is ignored.
                  </div>
                  <textarea
                    value={qcPasteText}
                    onChange={e => setQcPasteText(e.target.value)}
                    placeholder={'Packing Slip - 21/10/2025\nInvoice 2751\n100 Units Shipped\n43000055,43000081,...'}
                    rows={6}
                    className="input"
                    style={{ width: '100%', resize: 'vertical', fontFamily: 'var(--font-mono)', fontSize: 12, lineHeight: '16px', boxSizing: 'border-box' }} />
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <button className="btn btn-primary btn-sm"
                      disabled={!qcPasteText.trim()}
                      onClick={() => {
                        parseManifest(qcPasteText, 'pasted text');
                        setQcPasteOpen(false);
                        setQcPasteText('');
                      }}>Read serials</button>
                    <button className="btn btn-ghost btn-sm"
                      onClick={() => { setQcPasteText(''); setQcPasteOpen(false); }}>Cancel</button>
                  </div>
                </div>
              )}

              {!qcManifest && !qcManifestErr && !qcPasteOpen && (
                <div style={{ padding: '20px 16px' }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>No manifest attached</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4, lineHeight: '16px' }}>
                    Optional. Attach the supplier's manifest and it becomes the QC roster, so a short
                    delivery is caught before the invoice is paid. Upload a CSV or text file, or paste the
                    text straight out of a packing slip PDF. Without one, the roster builds as you scan.
                  </div>
                </div>
              )}

              {qcManifest && (<>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px',
                  background: 'var(--success-fill)', borderBottom: '1px solid var(--success-border)' }}>
                  <span style={{ fontSize: 14, color: 'var(--success-text)' }}>
                    <strong>{qcManifest.name}</strong> &middot; {qcManifest.rows.length} valid serial{qcManifest.rows.length === 1 ? '' : 's'}
                    {qcManifest.dupes.length > 0 && ' \u00b7 ' + qcManifest.dupes.length + ' duplicate' + (qcManifest.dupes.length === 1 ? '' : 's') + ' ignored'}
                    {qcManifest.invalid.length > 0 && ' \u00b7 ' + qcManifest.invalid.length + ' row' + (qcManifest.invalid.length === 1 ? '' : 's') + ' unreadable'}
                  </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,minmax(0,1fr))' }}>
                  {(() => {
                    const r = qcReconcile;
                    const diff = r.diff;
                    const tone = diff === null ? 'var(--text-muted)' : diff === 0 ? 'var(--success-text)' : 'var(--warning-text)';
                    return [
                      { label: 'On manifest', value: r.onManifest, sub: 'Becomes the QC roster', fg: 'var(--text-primary)', subFg: 'var(--text-muted)' },
                      { label: 'Ordered on the PO', value: r.ordered === null ? '\u2014' : r.ordered,
                        sub: r.ordered === null ? 'Enter units ordered' : 'From the purchase order',
                        fg: 'var(--text-primary)', subFg: 'var(--text-muted)' },
                      { label: 'Difference', value: diff === null ? '\u2014' : (diff > 0 ? '+' : '') + diff,
                        sub: diff === null ? 'Needs the order quantity' : diff === 0 ? 'Matches the order' : diff < 0 ? 'Short of the order' : 'More than ordered',
                        fg: tone, subFg: tone },
                    ].map((t, i) => (
                      <div key={t.label} style={{ padding: '14px 16px', borderRight: i < 2 ? '1px solid var(--border-subtle)' : 'none' }}>
                        <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-muted)' }}>{t.label}</div>
                        <div className="tnum" style={{ fontSize: 24, fontWeight: 500, lineHeight: 1, color: t.fg, marginTop: 8 }}>{t.value}</div>
                        <div style={{ fontSize: 12, color: t.subFg, marginTop: 6 }}>{t.sub}</div>
                      </div>
                    ));
                  })()}
                </div>
              </>)}
            </div>

            {/* Roster preview */}
            {qcManifest && (
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ height: 44, display: 'flex', alignItems: 'center', gap: 10, padding: '0 16px', borderBottom: '1px solid var(--border-default)' }}>
                  <span style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>Roster preview</span>
                  <span className="tnum" style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    {qcRosterPreview.length > 8 ? 'first 8 of ' + qcRosterPreview.length : qcRosterPreview.length + ' unit' + (qcRosterPreview.length === 1 ? '' : 's')}
                  </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '140px 100px 1fr 150px', gap: 12, height: 36,
                  background: 'var(--surface-inset)', borderBottom: '1px solid var(--border-default)', alignItems: 'center', padding: '0 16px' }}>
                  {['Serial', 'Type', 'Matched in Bench', 'State'].map(c => (
                    <div key={c} style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>{c}</div>
                  ))}
                </div>
                {qcRosterPreview.slice(0, 8).map(r => (
                  <div key={r.serial} style={{ display: 'grid', gridTemplateColumns: '140px 100px 1fr 150px', gap: 12, height: 40,
                    alignItems: 'center', padding: '0 16px', borderBottom: '1px solid var(--border-subtle)' }}>
                    <span className="tnum" style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)' }}>{r.serial}</span>
                    <span style={{ justifySelf: 'start', fontSize: 12, color: 'var(--text-muted)', background: 'var(--surface-inset)',
                      border: '1px solid var(--border-default)', borderRadius: 4, padding: '1px 6px' }}>{r.type}</span>
                    <span style={{ fontSize: 14, color: r.muted ? 'var(--text-ghost)' : 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.matched}</span>
                    <span className={'pill pill-' + r.stateTone + ' pill-dot'} style={{ justifySelf: 'start' }}>{r.state}</span>
                  </div>
                ))}
              </div>
            )}

            {card('What happens next', (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {[
                  ['1', 'Scan each unit in turn', 'The scanner reads the serial off the label; the roster builds as you go.'],
                  ['2', 'Walk its checklist', 'Keyboard-driven \u2014 P to pass, F to fail, N for not applicable, Enter for the next unit.'],
                  ['3', 'Import the delivery', 'Units that pass enter stock. Units that fail move to Pending RMA and are staged for the supplier claim.'],
                ].map(([n, title, body]) => (
                  <div key={n} style={{ display: 'flex', gap: 12 }}>
                    <div style={{ width: 22, height: 22, flexShrink: 0, borderRadius: 9999, background: 'var(--surface-inset)',
                      border: '1px solid var(--border-default)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>{n}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)' }}>{title}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2, lineHeight: '16px' }}>{body}</div>
                    </div>
                  </div>
                ))}
              </div>
            ))}

            {card('Ready to start', (
              <div>
                {(() => {
                  const blockers = [];
                  if (!qcBatchNo.trim()) blockers.push('Enter the purchase order or supplier reference.');
                  if (!qcType) blockers.push('Choose the device type being booked in.');
                  if (!qcSimType.trim()) blockers.push('Record SIM 1.');
                  if (qcNeedsSim2 && !qcSimType2.trim()) blockers.push('Record SIM 2.');
                  if (!qcPurchaseDate) blockers.push('Set the purchase date \u2014 warranty is calculated from it.');
                  else if (qcPurchaseDateErr) blockers.push(qcPurchaseDateErr);
                  if (isReadOnly) blockers.push('Your role cannot start a QC session.');
                  return (
                    <>
                      <button
                        onClick={() => {
                          setQcSessionId('qc-' + Date.now());
                          setQcStartedAt(new Date().toISOString());
                          if (qcManifest) {
                            const devs = [], res = {}, notes = {};
                            qcManifest.rows.forEach((r, n) => {
                              const lb = lbBySerial.current[normSerial(r.serial)] || {};
                              const tmpId = 'tmp-m' + n + '-' + Date.now();
                              devs.push({ tmpId, serial: r.serial, type: qcType,
                                lbDeviceId: lb.deviceId || '', firmware: lb.firmware || '',
                                fromManifest: true, scannedAt: null });
                              const seed = {};
                              getQCDefs(qcType).forEach(c => { seed[c.id] = c.type === 'number' ? 0 : ''; });
                              res[tmpId] = seed;
                              notes[tmpId] = '';
                            });
                            setQcDevs(devs); setQcRes(res); setQcNotes(notes);
                            setQcIdx(devs.length ? 0 : -1);
                          } else {
                            setQcIdx(-1);
                          }
                          setQcView('checking');
                        }}
                        disabled={blockers.length > 0}
                        className="btn btn-primary"
                        style={{ height: 40, fontWeight: 600, width: '100%' }}>Start QC</button>
                      {blockers.length > 0 && (
                        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {blockers.map(b => (
                            <div key={b} style={{ fontSize: 12, color: 'var(--text-muted)', display: 'flex', gap: 6 }}>
                              <span style={{ color: 'var(--danger-text)' }}>&bull;</span>{b}
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            ))}
          </div>
        </div>
      </>
    );
  }

  // ── Render: summary ───────────────────────────────────────────────────────
  if (qcView === 'summary') {
    if (qcDone) {
      return (
        <div>
          <div style={{ padding: 14, borderRadius: 'var(--radius-xl)', background: 'var(--success-fill)', border: '1px solid var(--success-border)', marginBottom: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--success-text)', marginBottom: 4 }}>
              {qcDone.total} device{qcDone.total === 1 ? '' : 's'} imported to batch {qcBatchNo.trim()}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {qcDone.passed} passed QC{qcDone.failed > 0 ? ` · ${qcDone.failed} failed and ${qcDone.failed === 1 ? 'is' : 'are'} staged for RMA under batch ${qcBatchNo.trim()}. Raise the return from the RMA screen so the claim stays tied to this delivery.` : '.'}
            </div>
          </div>
          {statsBlock(qcStats)}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={() => { const sess = (st.qcSessions || []).find(s => s.id === qcSessionId); if (sess) printQCSessionReport(sess); }} className="btn btn-ghost btn-sm">Print QC Report</button>
            {qcDone.failed > 0 && <button onClick={() => onViewRma && onViewRma()} className="btn btn-ghost btn-sm">Raise RMA for {qcDone.failed} failed</button>}
            <button onClick={() => { const b = qcBatchNo.trim(); resetAll(); onFinished && onFinished({ batchNo: b, sessionId: qcDone.sessionId }); }} className="btn btn-primary btn-sm">Done</button>
          </div>
        </div>
      );
    }
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <button onClick={() => setQcView('checking')} className="btn btn-ghost btn-sm">← Back to scanning</button>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Batch {qcBatchNo.trim()} · {qcType}</span>
        </div>
        {statsBlock(qcStats)}
        {qcStats.unmatched > 0 && (
          <div style={{ marginBottom: 12, padding: '10px 14px', borderRadius: 'var(--radius-lg)', background: 'var(--warning-fill)', border: '1px solid var(--warning-border)', color: 'var(--warning-text)', fontSize: 12 }}>
            {qcStats.unmatched} device{qcStats.unmatched === 1 ? '' : 's'} ha{qcStats.unmatched === 1 ? 's' : 've'} no Lightbug Device ID. They'll import with a blank Device ID and can be matched later with Sync with Lightbug.
          </div>
        )}
        <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid var(--border-default)', borderRadius: 8, marginBottom: 12 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <thead><tr style={{ background: darkMode ? 'rgba(255,255,255,0.04)' : '#f5f5f5' }}>
              {['Serial', 'Type', 'Lightbug ID', 'Result', 'Failed checks', 'Note', ''].map(h => (
                <th key={h} style={{ padding: '6px 10px', textAlign: 'left', color: 'var(--text-muted)', fontWeight: 600, borderBottom: '1px solid var(--border-default)' }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>{qcStats.rows.map(r => (
              <tr key={r.d.tmpId} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                <td style={{ padding: '6px 10px', fontWeight: 600, color: 'var(--text-default)' }}>{r.d.serial}</td>
                <td style={{ padding: '6px 10px', color: 'var(--text-muted)' }}>{r.d.type}</td>
                <td style={{ padding: '6px 10px', color: r.d.lbDeviceId ? 'var(--text-muted)' : 'var(--text-ghost)' }}>{r.d.lbDeviceId || '—'}</td>
                <td style={{ padding: '6px 10px' }}>{r.result === 'pass' ? pill('Pass', 'success') : pill('Fail', 'danger')}</td>
                <td style={{ padding: '6px 10px', color: 'var(--text-muted)' }}>{r.failedChecks.join(', ') || '—'}</td>
                <td style={{ padding: '6px 10px', color: 'var(--text-muted)' }}>{(qcNotes[r.d.tmpId] || '').slice(0, 40) || '—'}</td>
                <td style={{ padding: '6px 10px', textAlign: 'right' }}>
                  <button onClick={() => { setQcIdx(qcDevs.findIndex(d => d.tmpId === r.d.tmpId)); setQcView('checking'); }} className="btn btn-ghost btn-sm" style={{ padding: '2px 8px', fontSize: 11 }}>Edit</button>
                </td>
              </tr>
            ))}</tbody>
          </table>
        </div>
        {/* Normally supplied on the setup screen. These can still be blank here: a session
            resumed from a draft saved before the fields became mandatory, or — for SIM Type 2
            — a batch that had no RH2 in it at setup until one was switched in mid-scan.
            Offer the fields rather than dead-ending an operator who has scanned the lot. */}
        {(!qcPurchaseDate || qcPurchaseDateErr || qcSimMissing) && (
          <div style={{ marginBottom: 12, padding: 14, borderRadius: 'var(--radius-lg)', background: 'var(--warning-fill)', border: '1px solid var(--warning-border)' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--warning-text)', marginBottom: 4 }}>Batch details required</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 10 }}>
              These apply to every device in the batch and can't be left blank. Add them to import.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: isTablet ? '1fr' : '1fr 1fr', gap: 10 }}>
              {!qcSimType.trim() && (
                <div>
                  <label className="form-label">SIM Type *</label>
                  <input value={qcSimType} onChange={e => setQcSimType(e.target.value)} placeholder="e.g. Webbing" className="input" />
                </div>
              )}
              {qcNeedsSim2 && !qcSimType2.trim() && (
                <div>
                  <label className="form-label">SIM Type 2 *</label>
                  <input value={qcSimType2} onChange={e => setQcSimType2(e.target.value)} placeholder="e.g. Webbing" className="input" />
                </div>
              )}
              {(!qcPurchaseDate || qcPurchaseDateErr) && (
                <div>
                  <label className="form-label">Purchase Date *</label>
                  <DatePicker value={qcPurchaseDate} onChange={e => setQcPurchaseDate(e.target.value)} darkMode={darkMode} OW={OW} DM={DM} />
                  <div style={{ fontSize: 11, marginTop: 4, color: qcPurchaseDateErr ? 'var(--danger-text)' : 'var(--text-muted)' }}>
                    {qcPurchaseDateErr || 'Warranty is calculated from this.'}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        <button onClick={doImport} disabled={qcSaving || !qcDevs.length || !qcPurchaseDate || !!qcPurchaseDateErr || qcSimMissing} className="btn btn-primary">
          {qcSaving ? 'Importing…' : `Import ${qcDevs.length} Device${qcDevs.length === 1 ? '' : 's'}`}
        </button>
      </div>
    );
  }

  // ── Render: checking ──────────────────────────────────────────────────────
  // Two-pane workbench: the roster rail is always on screen, and qcIdx selects
  // which unit the main pane shows (-1 = nothing open, prompt to scan). Before
  // the reskin qcIdx swapped the whole screen between a scan station and a
  // checklist; it now drives selection instead. Every handler, guard and piece
  // of derived state below is the pre-existing one — only the layout changed.
  //
  // Below the tablet breakpoint the rail cannot stay open (see WorkbenchLayout),
  // so there the panes alternate exactly as they did before.

  const rosterUnits = qcStats.rows.map((row, i) => {
    const incomplete = row.unanswered > 0 && row.defs.length > 0;
    return {
      tmpId: row.d.tmpId,
      idx: i,
      serial: row.d.serial,
      selected: i === qcIdx,
      state: incomplete ? `${row.unanswered} left`
        : row.result === 'pass' ? 'Pass'
        : `Fail (${row.failedChecks.length || 1})`,
      tone: incomplete ? 'warning' : row.result === 'pass' ? 'success' : 'danger',
      bucket: incomplete ? 'todo' : row.result,
    };
  });

  // Progress counts completed units only. qcStats.pass/.fail classify every
  // scanned unit (a half-answered one reads as "pass" until something fails),
  // so feeding those straight to the bar would fill it on the first scan.
  const rosterDone = rosterUnits.filter(u => u.bucket !== 'todo');
  const donePass = rosterDone.filter(u => u.bucket === 'pass').length;
  const doneFail = rosterDone.filter(u => u.bucket === 'fail').length;

  const rosterFilters = [
    { value: 'all', label: `All ${rosterUnits.length}` },
    { value: 'todo', label: `To check ${rosterUnits.filter(u => u.bucket === 'todo').length}` },
    { value: 'fail', label: `Failed ${rosterUnits.filter(u => u.bucket === 'fail').length}` },
  ];
  const visibleUnits = rosterFilter === 'all' ? rosterUnits : rosterUnits.filter(u => u.bucket === rosterFilter);

  const rail = (
    <RosterRail
      title={qcBatchNo.trim() ? `Batch ${qcBatchNo.trim()}` : 'Batch'}
      total={qcDevs.length}
      checked={rosterDone.length}
      passed={donePass}
      failed={doneFail}
      scanRef={scanRef}
      scanValue={qcScanBuf}
      onScanInput={qcOnScanInput}
      onScanSubmit={() => qcCommitSerial(qcScanBuf)}
      filters={rosterFilters}
      activeFilter={rosterFilter}
      onFilter={setRosterFilter}
      units={visibleUnits}
      onSelect={tmpId => setQcIdx(qcDevs.findIndex(d => d.tmpId === tmpId))}
      emptyLabel="Scan a unit to begin. Each scan opens its checklist."
    />
  );

  const topbar = (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: 14, color: 'var(--text-muted)' }}>
        <span>Goods-in QC</span>
        <span style={{ color: 'var(--text-ghost)' }}>/</span>
        <span className="tnum" style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{qcBatchNo.trim() || '—'}</span>
        <span style={{ color: 'var(--text-ghost)' }}>/</span>
        <span style={{ color: 'var(--text-muted)' }}>{qcType}</span>
      </div>
      <div style={{ flex: 1 }} />
      {qcDraftSavedAt && (
        <span className="tnum" style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          Draft saved {new Date(qcDraftSavedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
        </span>
      )}
      <span className="tnum" style={{ fontSize: 13, color: 'var(--text-muted)' }}>
        {qcStats.total} checked · {qcStats.pass} pass / {qcStats.fail} fail
      </span>
      <button onClick={() => setQcCamOpen(v => !v)} className="btn btn-ghost btn-sm">
        {qcCamOpen ? 'Close camera' : 'Scan with camera'}
      </button>
      <button onClick={qcSyncLightbug} disabled={qcSyncing || !qcDevs.length} className="btn btn-ghost btn-sm">
        {qcSyncing ? 'Syncing…' : 'Sync with Lightbug'}
      </button>
      <button
        onClick={() => { if (window.confirm('Discard this QC session? Nothing will be imported.')) { discardDraft(); resetAll(); } }}
        className="btn btn-ghost btn-sm">Cancel</button>
      <button onClick={goToSummary} disabled={!qcDevs.length} className="btn btn-primary btn-sm">
        Finish{qcStats.incomplete > 0 ? ` · ${qcStats.incomplete} left` : ''}
      </button>
    </>
  );

  // Messages that belong to the session rather than to one unit.
  const sessionBanners = (
    <>
      {qcSyncMsg && (
        <div role="status" style={{
          fontSize: 12, color: qcSyncMsg.startsWith('Sync failed') ? 'var(--danger-text)' : 'var(--text-muted)',
        }}>{qcSyncMsg}</div>
      )}
      {qcCamOpen && (
        <div style={{ borderRadius: 'var(--radius-md)', overflow: 'hidden', border: '1px solid var(--border-default)', background: '#111', maxHeight: 220 }}>
          <PhoneScannerScreen compact onScan={qcCommitSerial} />
        </div>
      )}
      {qcScanErr && (
        <div role="alert" style={{
          padding: 'var(--space-2) var(--space-3)', borderRadius: 'var(--radius-md)',
          background: 'var(--danger-fill)', border: '1px solid var(--danger-border)',
          color: 'var(--danger-text)', fontSize: 12, fontWeight: 500,
          display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
        }}>
          <span style={{ flex: 1 }}>{qcScanErr}</span>
          <button onClick={() => setQcScanErr('')} aria-label="Dismiss scan error"
            style={{ background: 'transparent', border: '1px solid var(--danger-border)', borderRadius: 'var(--radius-md)',
              color: 'var(--danger-text)', padding: '2px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
            Dismiss
          </button>
        </div>
      )}
    </>
  );

  // Main pane with nothing open: on desktop this sits beside the roster, so it
  // is a prompt rather than a screen of its own.
  const emptyPane = (
    <div className="card" style={{
      flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: 'var(--space-2)',
      padding: 'var(--space-8)', textAlign: 'center',
    }}>
      <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>Scan the next unit</div>
      <div style={{ fontSize: 13, color: 'var(--text-muted)', maxWidth: 380, lineHeight: '18px' }}>
        Each scan opens that unit's checklist. Units that pass every check return here automatically;
        a failure holds the screen so you can record the fault.
      </div>
    </div>
  );

  if (!curDev) {
    return (
      <WorkbenchLayout topbar={topbar} rail={rail} stacked={isTablet}>
        {sessionBanners}
        {emptyPane}
        <KeyHintBar hints={[
          { key: 'Enter', label: 'Look up the scanned serial' },
          { key: '↑ ↓', label: 'Move through the roster' },
        ]} />
      </WorkbenchLayout>
    );
  }

  const r = qcRes[curDev.tmpId] || {};
  const dr = devResult(curDev);
  const anyFail = dr.failedChecks.length > 0;

  const mainPane = (
    <>
      <div className="card" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: 0 }}>
        {/* Unit identity */}
        <div style={{
          flexShrink: 0, padding: 'var(--space-4)', borderBottom: '1px solid var(--border-default)',
          display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap',
        }}>
          <button onClick={() => setQcIdx(i => Math.max(0, i - 1))} disabled={qcIdx === 0}
            aria-label="Previous unit" className="btn btn-ghost btn-sm">◂</button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
              <span className="tnum" style={{ fontSize: 24, fontWeight: 600, letterSpacing: '-0.3px', color: 'var(--text-primary)' }}>
                {curDev.serial}
              </span>
              <span style={{
                fontSize: 12, color: 'var(--text-muted)', background: 'var(--surface-inset)',
                border: '1px solid var(--border-default)', padding: '2px 8px', borderRadius: 'var(--radius-sm)',
              }}>{curDev.type}</span>
              {!curDev.lbDeviceId && pill('Not found in Lightbug', 'warning')}
              {curDev.alreadyExists && pill('Already in OWL Bench', 'warning')}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
              {curDev.lbDeviceId ? `Lightbug ${curDev.lbDeviceId} · ` : ''}
              {curDev.firmware ? `FW ${curDev.firmware} · ` : ''}
              unit {qcIdx + 1} of {qcDevs.length}
            </div>
          </div>
          <span className="tnum" style={{ fontSize: 14, color: 'var(--text-muted)' }}>
            {curDefs.length ? `Check ${Math.min(dr.answered + 1, curPF.length)} of ${curPF.length}` : ''}
          </span>
          <button onClick={() => setQcIdx(i => Math.min(qcDevs.length - 1, i + 1))} disabled={qcIdx >= qcDevs.length - 1}
            aria-label="Next unit" className="btn btn-ghost btn-sm">▸</button>
        </div>

        {qcAllowMixedType && (
          <div style={{ flexShrink: 0, padding: 'var(--space-3) var(--space-4)', borderBottom: '1px solid var(--border-subtle)', maxWidth: 280 }}>
            <label className="form-label">Device Type</label>
            <FilterSelect value={curDev.type} onChange={v => changeDevType(curDev.tmpId, v)} darkMode={darkMode} OW={OW} DM={DM}
              options={(st.deviceTypes || []).map(t => ({ value: t, label: t }))} />
          </div>
        )}

        {anyFail && (
          <div role="status" style={{
            flexShrink: 0, padding: 'var(--space-2) var(--space-4)', background: 'var(--warning-fill)',
            borderBottom: '1px solid var(--warning-border)', color: 'var(--warning-text)',
            fontSize: 12, fontWeight: 500,
          }}>
            Keep going — record every issue so the RMA lists them all.
          </div>
        )}

        {/* Checklist */}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {curDefs.length === 0 ? (
            <div style={{ padding: 'var(--space-4)' }}>
              <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 'var(--space-2)' }}>
                No QC checks defined for {curDev.type} — record any issues in the fault box below.
              </div>
              <button
                onClick={() => setQcDevs(p => p.map(d => d.tmpId === curDev.tmpId ? { ...d, manualFail: !d.manualFail } : d))}
                aria-pressed={!!curDev.manualFail}
                className={curDev.manualFail ? 'btn btn-sm' : 'btn btn-ghost btn-sm'}
                style={curDev.manualFail ? { background: 'var(--danger-fill)', color: 'var(--danger-text)', border: '1px solid var(--danger-border)' } : {}}>
                {curDev.manualFail ? '✗ Marked as Failed' : 'Mark as Failed'}
              </button>
            </div>
          ) : curDefs.map((t, i) => (
            <CheckRow
              key={t.id}
              def={t}
              value={r[t.id]}
              index={i}
              total={curDefs.length}
              focused={i === focusedCheck}
              options={CHECK_OPTIONS}
              onAnswer={v => { setFocusedCheck(i); setQcChk(curDev.tmpId, t.id, v); }}
              onClear={() => { setFocusedCheck(i); setQcChk(curDev.tmpId, t.id, ''); }}
            />
          ))}
        </div>

        {/* Fault note + close */}
        <div style={{
          flexShrink: 0, padding: 'var(--space-3) var(--space-4)', borderTop: '1px solid var(--border-default)',
          display: 'flex', alignItems: 'flex-end', gap: 'var(--space-3)', flexWrap: 'wrap',
        }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <label className="form-label" htmlFor="qc-fault-note">
              {anyFail || curDev.manualFail ? 'What is wrong with it' : 'Notes'}{' '}
              <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>
                {anyFail || curDev.manualFail ? '(becomes the RMA fault description)' : '(optional)'}
              </span>
            </label>
            <input
              id="qc-fault-note"
              ref={noteRef}
              value={qcNotes[curDev.tmpId] || ''}
              onChange={e => setQcNotes(n => ({ ...n, [curDev.tmpId]: e.target.value }))}
              placeholder={anyFail ? 'Describe the fault for the RMA…' : 'Anything worth noting…'}
              className="input"
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <span className="tnum" style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {curDefs.length ? `${dr.answered} of ${curPF.length} answered` : ''}
            </span>
            <button onClick={() => removeDev(curDev.tmpId)} className="btn btn-ghost btn-sm">Remove</button>
            <button onClick={() => { setQcIdx(-1); setQcScanBuf(''); }} className="btn btn-primary">Close the unit</button>
          </div>
        </div>
      </div>

      <KeyHintBar hints={[
        { key: 'P', label: 'Pass' },
        { key: 'F', label: 'Fail' },
        { key: 'N', label: 'Not applicable' },
        { key: '↑ ↓', label: 'Move between checks' },
        { key: 'Enter', label: 'Close the unit' },
        { key: 'Esc', label: 'Back to the roster' },
      ]} />
    </>
  );

  return (
    <WorkbenchLayout topbar={topbar} rail={isTablet ? null : rail} stacked={isTablet}>
      {sessionBanners}
      {mainPane}
    </WorkbenchLayout>
  );
}
